
import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { OnboardingForm } from "@/types/onboarding";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface PicSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  form: OnboardingForm | null;
  onSave: (picArray: string[], picId: string | null, campaignBU: string) => void;
}

// Campaign BU options - match with the specific list provided
const CAMPAIGN_BU_OPTIONS = [
  { value: 'Event - Rewards Station', label: 'Event - Rewards Station' },
  { value: 'Token - MNT Token Mindshare', label: 'Token - MNT Token Mindshare' },
  { value: 'BU - FBTC', label: 'BU - FBTC' },
  { value: 'BU - Mantle Network (includes L2 Eco Dapps)', label: 'BU - Mantle Network (includes L2 Eco Dapps)' },
  { value: 'BU - mETH (includes COOK & cmETH)', label: 'BU - mETH (includes COOK & cmETH)' },
  { value: 'BU - Index Fund', label: 'BU - Index Fund' },
  { value: 'BU - Mantle Banking', label: 'BU - Mantle Banking' },
  { value: 'BU - Mantle Group', label: 'BU - Mantle Group' },
  { value: 'BU - MantleX', label: 'BU - MantleX' },
  { value: 'Others', label: 'Others' },
];

export const PicSelectorDialog: React.FC<PicSelectorDialogProps> = ({ 
  open, 
  onClose, 
  form,
  onSave
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [picOptions, setPicOptions] = useState<{value: string, id: string, label: string}[]>([]);
  const [selectedPicName, setSelectedPicName] = useState<string>("");
  const [selectedPicId, setSelectedPicId] = useState<string | null>(null);
  const [selectedCampaignBU, setSelectedCampaignBU] = useState<string>("Event - Rewards Station");
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchPICOptions();
      
      // Reset selection when dialog opens
      setSelectedPicName("");
      setSelectedPicId(null);
      setSelectedCampaignBU("Event - Rewards Station");
    }
  }, [open]);

  const fetchPICOptions = async () => {
    try {
      setIsLoading(true);
      const { data: picData, error } = await supabase
        .from('kol_pic')
        .select('id, name')
        .eq('position', 'regional_manager') // Filter by regional_manager position
        .order('name');

      if (error) throw error;

      const options = picData.map(pic => ({
        value: pic.name,
        id: pic.id,
        label: pic.name
      }));
      
      setPicOptions(options);
    } catch (error) {
      console.error("Error fetching PIC options:", error);
      toast({
        title: "Error fetching PIC managers",
        description: "Could not load the list of PIC managers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePicSelectionChange = (name: string) => {
    setSelectedPicName(name);
    
    // Find corresponding ID for the selected PIC
    const picEntity = picOptions.find(pic => pic.value === name);
    setSelectedPicId(picEntity?.id || null);
  };

  const handleSave = () => {
    if (!selectedPicName) {
      toast({
        title: "No PIC selected",
        description: "Please select a PIC manager",
        variant: "destructive",
      });
      return;
    }
    
    // We now send an array with a single value to maintain compatibility with existing code
    onSave([selectedPicName], selectedPicId, selectedCampaignBU);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign PIC Manager</DialogTitle>
          <DialogDescription>
            Select PIC manager who will be responsible for this creator: {form?.kol_name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="pic-select">PIC Manager</Label>
                <Select 
                  value={selectedPicName} 
                  onValueChange={handlePicSelectionChange}
                >
                  <SelectTrigger id="pic-select" className="w-full">
                    <SelectValue placeholder="Select a PIC manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {picOptions.map(option => (
                      <SelectItem key={option.id} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="campaign-bu">Campaign BU</Label>
                <Select 
                  value={selectedCampaignBU} 
                  onValueChange={setSelectedCampaignBU}
                >
                  <SelectTrigger id="campaign-bu" className="w-full">
                    <SelectValue placeholder="Select Campaign BU" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_BU_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isLoading || !selectedPicName}
          >
            Assign and Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
